import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import api from '../../utils/api';

export default function HelpDeskTicketList({ title, scope, emptyHint }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/helpdesk/tickets', {
        params: {
          scope,
          page: page + 1,
          limit: rowsPerPage,
        },
      });
      const raw = r.data?.data;
      const list = Array.isArray(raw) ? raw : [];
      setRows(list);
      setTotal(r.data?.pagination?.total ?? 0);
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [scope, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get('/helpdesk/ticket-statuses')
      .then((r) => setStatusOptions(r.data?.data?.options || []))
      .catch(() => setStatusOptions([]));
  }, []);

  const statusMap = useMemo(() => {
    const m = new Map();
    for (const s of statusOptions) m.set(String(s.code), s);
    return m;
  }, [statusOptions]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      {emptyHint && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {emptyHint}
        </Typography>
      )}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ticket</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow
                      key={t.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/helpdesk/${t.id}`)}
                    >
                      <TableCell>{t.ticket_number}</TableCell>
                      <TableCell>{t.subject}</TableCell>
                      <TableCell>{t.severity?.name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusMap.get(t.status)?.label || t.status}
                          color={statusMap.get(t.status)?.ui_color || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {t.owner
                          ? `${t.owner.first_name} ${t.owner.last_name}`
                          : 'Not assigned'}
                      </TableCell>
                      <TableCell>
                        {t.updatedAt
                          ? new Date(t.updatedAt).toLocaleString()
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!rows.length && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No tickets
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
